import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { ButtonHTMLAttributes, MouseEvent, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useMutation } from 'react-query'

// component
import Icon from '@/assets/icons'
import AccessibleIcon from '@/components/common/AccessibleIcon'
import { SearchBar } from '../../../../../shared/livemallclub/SearchBar'
import BarItem from './components/BarItem'
import PreferenceMenu from './components/PreferenceMenu'

// Libs
import useLiveMallSearch from '@/hooks/useLiveMallSearch'
import { useMemberData } from '@/hooks/useMemberData'
import { usePreferences } from '@/hooks/usePreferences'
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'
import { TPreference } from '@/types/preference-tag'

// Styles
import { setMemberPreferenceTag } from '@/api/setMemberPreferenceTag'
import { ROUTE_PATH } from '@/constants/routePath'
import styles from './style.module.scss'

const cx = classnames.bind(styles)

export const SearchButton = (props: ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button className={cx('preferences-bar__icon', 'magnifier')} {...props}>
      <AccessibleIcon label='search'>
        <Icon.SearchMagnifier />
      </AccessibleIcon>
    </button>
  )
}

const Preferences = () => {
  const { t } = useTranslation()
  const { memberPreferencesTagList } = usePreferences()
  const { memberData } = useMemberData()
  const router = useRouter()

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isSearching, setIsSearching] = useState(false)

  const { preferenceTag: preferenceTagFromQuery } = router.query
  const setPreferenceTagMutation = useMutation(setMemberPreferenceTag)

  const activePreferencesTag = memberPreferencesTagList.find((item) => item.preferenceTag === Number(preferenceTagFromQuery))?.preferenceTag

  const { handleSubmit, inputRef, clearInput } = useLiveMallSearch()

  const handleBarItemClick = (data: TPreference) => {
    handleRoutePushWithQuery({
      router,
      pathname: router.pathname,
      query: { preferenceTag: data.preferenceTag },
      isReplace: true,
    })
  }
  const handleEditModeClick = () => {
    setIsEditMode(!isEditMode)
  }

  const handleMenuSwitchClick = (event: MouseEvent) => {
    event.stopPropagation()
    document.body.style.overflow = isMenuOpen ? 'auto' : 'hidden'
    setIsMenuOpen(!isMenuOpen)
  }

  const handleSearchButtonClick = () => {
    router.push({
      pathname: ROUTE_PATH.SEARCH,
      query: { category: 'all', keyword: '' },
    })
  }

  useEffect(() => {
    if (memberData.isTagSetup === false) {
      setIsMenuOpen(true)

      // 第一次顯示 menu 後，打一個空的 preference tag list，is_tag_setup 會變成 true
      setPreferenceTagMutation.mutateAsync({
        preferenceTagList: [],
      })
    }
  }, [memberData.isTagSetup])

  return (
    <div className={cx('preferences', { isMenuOpen }, { isSearching })}>
      <PreferenceMenu isOpen={isMenuOpen} isEditMode={isEditMode} />
      <div className={cx('preferences-bar-wrapper')}>
        <SearchButton onClick={handleSearchButtonClick} />
        {isSearching && (
          <SearchBar
            inputRef={inputRef}
            onSubmit={handleSubmit}
            onClear={clearInput}
            showSearchIcon={false}
            handleHideSearchBar={() => setIsSearching(false)}
            showCancelButton
          />
        )}
        {isMenuOpen && (
          <button className={cx('preferences-bar-button')} onClick={handleEditModeClick}>
            {isEditMode ? t('Common_Done') : t('Common_Preferences_Edit')}
          </button>
        )}
        {!isSearching && (
          <div className={cx('preferences-bar')}>
            <div className={cx('preferences-bar__tags')}>
              {memberPreferencesTagList.map((tag, index) => (
                <BarItem {...tag} key={index} handleItemClick={handleBarItemClick} isActive={tag.preferenceTag === activePreferencesTag} />
              ))}
            </div>
          </div>
        )}
      </div>
      {!isSearching && (
        <>
          <div className={cx('preferences-bar-mask')} />
          <div className={cx('preferences-bar__icon', 'menu-switch')} onClick={handleMenuSwitchClick}>
            <AccessibleIcon label='menu-switch'>
              <Icon.MenuArrow />
            </AccessibleIcon>
          </div>
        </>
      )}
    </div>
  )
}

export default Preferences
